import { useMemo } from "react";
import { useMasterData } from "../api/useMasterData";
import { selectClientIdBillingReport, selectDateRangeReport, selectDeceaseName, selectSelectedChapelReport, selectSelectedClientIdReport, selectSelectedClientReport, selectTypeOfBillingReport, setSelectedBillingReportTypes } from "../../features/reports/reportsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useGetReportByDateRangeAndChapelsQuery } from "../../features/reports/reportsApiSlice";
import { getRtkErrorMessage } from "../../utils/getRtkErrorMessage";
import { showToast } from "../../toastify/Toast";
import { formatDateRange, formatDateRangeReport, formatTimeRange } from "../../utils/formatSchedule";
import { useMutationData } from "../api/useMutationData";
import { handleApiError } from "../../utils/handleApiError";
import { formatScheduleForReport } from "../../utils/formatDateAndTime";
import { selectCurrentClientId } from "../../features/client/clientSlice";
import { clearTypeOfAdditionalModal } from "../../features/modal/modalSlice";

export default function useReport() {

    const dispatch = useDispatch()
    const selectedChapel = useSelector(selectSelectedChapelReport)
    const selectedClient = useSelector(selectSelectedClientReport)
    const selectedClientId = useSelector(selectSelectedClientIdReport)
    const clientId = useSelector(selectCurrentClientId)
    const clientIdBillingReport = useSelector(selectClientIdBillingReport)
    const { chapelsAvailable } = useMasterData()
    const dateRange = useSelector(selectDateRangeReport)
    const selectedTypes = useSelector(selectTypeOfBillingReport);
    const selectedDeceaseName = useSelector(selectDeceaseName);
    const { exportSalesExcel, exportBillingReport, isLoadingExportBillingReport, } = useMutationData()

    const simplifiedChapels = useMemo(() =>
        chapelsAvailable.map(({ chapelID, chapelName }) => ({
            chapelID,
            chapelName,
        })), [chapelsAvailable]
    );

    const selectedChapelNames = useMemo(() => {
        return `Chapel ${selectedChapel
            .map(c => c.chapelName.match(/\d+/)?.[0] ?? c.chapelName)
            .join("-")}`;
    }, [selectedChapel]);

    const isExportBtnDisable: boolean = !dateRange.from || !dateRange.to || selectedChapel.length === 0

    const chapelIds = useMemo(
        () => selectedChapel.map(c => c.chapelID),
        [selectedChapel]
    );

    const reportParams = useMemo(() => ({
        startDate: dateRange.from,
        endDate: dateRange.to,
        chapelIds,
    }), [dateRange, chapelIds]);

    const reportOptions = useMemo(() => ({
        skip: !dateRange.from || !dateRange.to || chapelIds.length === 0,
    }), [dateRange, chapelIds]);

    const { data: dataReportByDateRangeAndChapelIds, isLoading: isLoadingReportByDateRangeAndChapelIds, isError: isErrorReportByDateRangeAndChapelIds, error: errorReportByDateRangeAndChapelIds } = useGetReportByDateRangeAndChapelsQuery(reportParams, reportOptions);
    const errorMessageReportReportByDateRangeAndChapelIds = getRtkErrorMessage(errorReportByDateRangeAndChapelIds);

    const hasNoData = !dataReportByDateRangeAndChapelIds?.items?.length;
    const summary = dataReportByDateRangeAndChapelIds?.summary;
    const reportStats = {
        amountToBeBilled: summary?.amountToBeBilled ?? 0,
        totalCupsConsumed: summary?.totalCupsConsumed ?? 0,
        actualSales: summary?.totalPackageSales ?? 0,
        numberOfClients: summary?.totalClients,
        numberOfGuest: summary?.totalUser,
        isNullSummaryGrandTotal: summary?.grandTotal === null,
        isNullSummaryTotalClients: summary?.totalClients === null,
        isNullSummaryTotalSalesBase: summary?.totalSalesBase === null,
        isNullSummaryTotalUser: summary?.totalUser === null,
        isNullSummaryTotalWithVat: summary?.totalWithVat === null,
        isNullSummaryVatAmount: summary?.vatAmount === null,
    }

    const sortedClients = useMemo(() => {
        return [...(dataReportByDateRangeAndChapelIds?.items ?? [])].sort((a, b) => {
            const dateA = new Date(a.scheduleFrom.replace(" ", "T"));
            const dateB = new Date(b.scheduleFrom.replace(" ", "T"));

            return dateA.getTime() - dateB.getTime();
        });
    }, [dataReportByDateRangeAndChapelIds?.items]);

    const handleGenerateReport = async () => {
        try {
            const startDate = dateRange.from;
            const endDate = dateRange.to;
            if (isExportBtnDisable) {
                return showToast('Please select a date range and at least one chapel.', 'error')
            }

            const dateRangeFormat = formatDateRangeReport(startDate, endDate)
            const filename = `Heritage - Sales Report ${dateRangeFormat} - ${selectedChapelNames}.xlsx`; 
            
            const body = { startDate, endDate, chapelIds: chapelIds, }
            // Call the mutation and unwrap to get the Blob
            const blob = await exportSalesExcel(body).unwrap();

            // Trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            showToast("Sales report exported successfully!", "success");
        } catch (error) {
            handleApiError(error, "Failed to export report.");
        }
    }

    const initialName = selectedClient?.deceasedName[0];
    const scheduleFrom = selectedClient?.scheduleFrom;
    const scheduleTo = selectedClient?.scheduleTo;
    const dateRangeFormat = formatDateRange(scheduleFrom ?? '', scheduleTo ?? '');
    const timeRangeFormat  = formatTimeRange(scheduleFrom ?? '', scheduleTo ?? '');
    const deceasedName = selectedClient?.deceasedName.toUpperCase() ?? '';
    const chapelName = selectedClient?.chapelName ?? '';
    const amountToBeBilled = selectedClient?.amountToBeBilled ?? 0;
    const totalPackageSales = selectedClient?.totalPackageSales ?? 0
    const clientPackages = selectedClient?.packageNames
    const isExportClientBtnDisable = !dateRange.from || !dateRange.to || !selectedClientId;

    const handleGenerateReportClient = async () => {   
        if(!selectedClientId || !selectedClient || !scheduleFrom || !scheduleTo) return

        try {
            const startDate = dateRange.from
            const endDate = dateRange.to
            if (isExportBtnDisable) return

            const dateRangeFormat = formatScheduleForReport(scheduleFrom, scheduleTo);
            const safeDateRange = dateRangeFormat.replace(/:/g, ".");
            const filename = `Heritage - Sales Report ${deceasedName} ${safeDateRange}.xlsx`;

            // Call the mutation and unwrap to get the Blob
            const blob = await exportSalesExcel({
                startDate,
                endDate,
                clientID: selectedClientId
            }).unwrap();

            // Trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showToast("Report exported successfully!", "success");
        } catch (error) {
            handleApiError(error, "Failed to export report.");
        }
    };

    const generateBillingReport = async () => {
        if(!clientIdBillingReport || !selectedDeceaseName) return;
        try {
            const params: { clientId: number; type?: "summary" | "detailed"; } = { clientId: clientIdBillingReport };
            if (selectedTypes.length === 1) {
                params.type = selectedTypes[0];
            }
            const blob = await exportBillingReport(params).unwrap();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const fileName =
            selectedTypes.length === 2
                ? `Billing Report - ${selectedDeceaseName}.xlsx`
                : `${selectedTypes[0] === "summary" ? "Summary" : "Detailed"} Billing Report - ${selectedDeceaseName}.xlsx`;

            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
            dispatch(clearTypeOfAdditionalModal())
            dispatch(setSelectedBillingReportTypes(['detailed', 'summary']))
        } catch (error) {
            handleApiError(error, "Failed to export report.");
        }
    }


    return {
        simplifiedChapels,
        selectedChapelNames,
        isExportBtnDisable,
        chapelIds,
        reportParams,

        dataReportByDateRangeAndChapelIds,
        isLoadingReportByDateRangeAndChapelIds,
        isErrorReportByDateRangeAndChapelIds,
        errorMessageReportReportByDateRangeAndChapelIds,

        reportStats,
        hasNoData,
        sortedClients,

        handleGenerateReport,

        handleGenerateReportClient,
        isExportClientBtnDisable,
        initialName,
        scheduleFrom,
        scheduleTo,
        dateRangeFormat,
        timeRangeFormat,
        deceasedName,
        amountToBeBilled,
        totalPackageSales,
        chapelName,
        clientPackages,
        selectedClientId,

        generateBillingReport,
        isLoadingExportBillingReport,
    }
}